import { ChangeDetectionStrategy, Component, computed, effect, input, output } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';

import { ButtonComponent } from '../../button/button.component';
import { IconComponent } from '../../icons/icon/icon.component';
import { TOASTER_STATUS, type ToastAction, type ToasterStatus } from '../toaster.types';

const STATUS_ICON: Record<ToasterStatus, string> = {
    [TOASTER_STATUS.SUCCESS]: 'assets/icons/check.svg',
    [TOASTER_STATUS.ERROR]: 'assets/icons/alert.svg',
    [TOASTER_STATUS.INFO]: 'assets/icons/infos.svg',
};

const DEFAULT_DURATION_MS = 5000;

@Component({
    selector: 'app-toaster',
    templateUrl: './toaster.component.html',
    styleUrl: './toaster.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [TranslatePipe, IconComponent, ButtonComponent],
    host: {
        role: 'status',
        'aria-live': 'polite',
        'aria-atomic': 'true',
        'animate.enter': 'toaster-enter',
        'animate.leave': 'toaster-leave',
    },
})
export class ToasterComponent {
    protected readonly TOASTER_STATUS = TOASTER_STATUS;

    readonly status = input<ToasterStatus>(TOASTER_STATUS.SUCCESS);
    readonly title = input<string>('');
    readonly subtitle = input<string>('');
    readonly message = input<string>('');
    readonly action = input<ToastAction | undefined>(undefined);
    readonly clickHandler = input<(() => void) | undefined>(undefined);
    readonly duration = input<number>(DEFAULT_DURATION_MS);
    readonly restartToken = input<number>(0);

    readonly closed = output<void>();

    protected readonly icon = computed(() => STATUS_ICON[this.status()]);

    private timeoutRef?: ReturnType<typeof setTimeout>;

    constructor() {
        effect((onCleanup) => {
            this.restartToken();
            const delay = this.duration();

            clearTimeout(this.timeoutRef);
            if (delay > 0) {
                this.timeoutRef = setTimeout(() => this.close(), delay);
            }

            onCleanup(() => clearTimeout(this.timeoutRef));
        });
    }

    close(): void {
        clearTimeout(this.timeoutRef);
        this.closed.emit();
    }

    protected onActionClick(event: MouseEvent): void {
        event.stopPropagation();
        this.action()?.run();
        this.close();
    }

    protected onCardClick(): void {
        const handler = this.clickHandler();
        if (!handler) {
            return;
        }
        handler();
        this.close();
    }
}
